import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Check, List, Cpu, Zap, ShieldCheck, Settings, Info, Activity, Gauge, Monitor, Layout, RefreshCw, Smartphone, Play, Search, UserCheck } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "IPTV em Google TV", href: "/blog/iptv-google-tv" }
  ];

  const faq = [
    {
      question: "Qual a diferença entre Google TV e Android TV?",
      answer: "O Google TV é uma evolução da interface do Android TV. Ele foca em recomendações de conteúdo em vez de apenas listar aplicativos. Por baixo, o sistema continua sendo o Android, o que garante 100% de compatibilidade com apps de IPTV."
    },
    {
      question: "Dá para instalar o One X no Chromecast com Google TV?",
      answer: "Sim! O Chromecast 4 (HD ou 4K) roda perfeitamente os aplicativos oficiais da P2 PRO através do método de instalação via Downloader."
    },
    {
      question: "Como habilitar o 'Modo Desenvolvedor' no Google TV?",
      answer: "Vá em Configurações {'>'} Sistema {'>'} Sobre {'>'} Clique no 'Número da Versão' 7 vezes até aparecer a mensagem de desenvolvedor. Isso é essencial para instalar apps de fora da loja."
    },
    {
      question: "O Google TV apaga apps de IPTV?",
      answer: "O Google possui um sistema de segurança chamado 'Play Protect'. Se ele identificar um app como desconhecido, pode dar um alerta, mas você pode permitir o uso manualmente nas configurações de segurança."
    }
  ];

  const benefits = [
    {
      title: "Interface Agregadora",
      description: "Organize seus canais favoritos e filmes em uma tela inicial inteligente e moderna.",
      icon: <Layout className="w-6 h-6" />
    },
    {
      title: "Perfis Individuais",
      description: "Crie perfis separados para cada membro da família, cada um com sua lista IPTV personalizada.",
      icon: <UserCheck className="w-6 h-6" />
    },
    {
      title: "Busca Inteligente",
      description: "Encontre filmes do catálogo P2 PRO usando a busca unificada do Google.",
      icon: <Search className="w-6 h-6" />
    }
  ];

  const howItWorks = [
    {
      title: "Habilitar Desenvolvedor",
      description: "Libere as opções ocultas do sistema para ganhar controle total."
    },
    {
      title: "Permissão de Fontes",
      description: "Autorize o aplicativo Downloader a instalar arquivos externos."
    },
    {
      title: "Transferência de APK",
      description: "Baixe a versão mais recente do player One X direto para o seu Google TV."
    },
    {
      title: "Otimização de Cache",
      description: "Ajuste o armazenamento para garantir que o sistema não fique lento com o uso."
    }
  ];

  const diferenciais = [
    {
      title: "AI Recommendations",
      description: "O sistema Google TV aprende seus hábitos de IPTV e sugere categorias similares na home.",
      icon: <Cpu className="w-6 h-6" />
    },
    {
      title: "Multi-User Control",
      description: "Gerencie o acesso das crianças aos canais adultos através do controle parental do Google.",
      icon: <ShieldCheck className="w-6 h-6" />
    }
  ];

  const sections = [
    {
      title: "1. Introdução: A Nova Face do Entretenimento Google",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            O Google TV chegou para mudar a forma como interagimos com o streaming. Ao contrário de seu antecessor, ele não é apenas um lançador de apps, mas um curador de conteúdo. Para o usuário da <strong>P2 PRO</strong>, isso significa integrar nossa vasta biblioteca de canais e VODs a uma das interfaces mais bonitas e fluidas do mercado. Se você tem um Chromecast de quarta geração ou uma Smart TV Sony/TCL de última geração, você está usando o Google TV.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Neste guia exaustivo, vamos ensinar você a configurar o IPTV neste ecossistema de elite. Explicamos como driblar as novas travas de segurança do Google, como otimizar a interface 'pesada' do sistema e como garantir que o sinal 4K da P2 PRO brilhe com 100% de estabilidade.
          </p>
        </div>
      )
    },
    {
      title: "2. Superando as Travas do Google TV",
      type: "featured" as const,
      content: (
        <div className="space-y-8">
          <div className="bg-black/30 p-8 rounded-3xl border border-white/5">
            <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Settings className="w-6 h-6 text-electric" /> O Protocolo de Segurança</h4>
            <p className="text-muted-foreground mb-6">O Google TV é mais rígido com 'Fontes Desconhecidas'. Siga este passo a passo para instalar o <strong>One X</strong> ou <strong>P2 PLAY</strong>:</p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 bg-white/5 rounded-2xl border border-white/10">
                <h5 className="font-bold text-electric mb-2">Habilitar Apps Fora da Loja</h5>
                <p className="text-sm text-muted-foreground">Vá em Configurações {'>'} Apps {'>'} Segurança e Restrições {'>'} Fontes Desconhecidas. O botão estará 'cinza' até você habilitar o modo desenvolvedor nas configurações de 'Sobre'.</p>
              </div>
              <div className="p-6 bg-white/5 rounded-2xl border border-white/10">
                <h5 className="font-bold text-electric mb-2">Desativar Scan do Play Protect</h5>
                <p className="text-sm text-muted-foreground">Para que o app de IPTV não seja sinalizado por engano, entre na Play Store {'>'} Perfil {'>'} Play Protect {'>'} Engrenagem {'>'} Desative 'Verificar apps com Play Protect'.</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "3. Otimização de Performance: Otimizando o Hardware",
      content: (
        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-muted-foreground">O Google TV tem uma interface gráfica muito rica, o que consome muita memória RAM. Siga este tuning P2 PRO:</p>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">!</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Habilite o 'Modo Apenas Apps'</h4>
              <p className="text-muted-foreground">Se você sente que o Google TV está lento, vá em Configurações {'>'} Contas {'>'} Seu Perfil {'>'} Habilite 'Modo Apenas Apps'. Isso desativa as recomendações pesadas do Google e libera 30% mais RAM para o seu IPTV rodar sem travamentos.</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">!</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Limpeza do 'Ambient Mode'</h4>
              <p className="text-muted-foreground">O protetor de tela do Google TV baixa fotos de alta resolução da internet o tempo todo. Desative as fotos do Google Photos no modo ambiente para economizar banda de rede para o streaming 4K.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "4. Auditoria de Rede no Chromecast e Google TV",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">O Chromecast com Google TV tem uma antena Wi-Fi pequena que pode ser um gargalo. Use estas diretrizes:</p>
          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Use Adaptador Ethernet (Cabo)</h5>
                <p className="text-sm text-muted-foreground">Se possível, compre o adaptador oficial ou um Hub USB-C que tenha entrada RJ45. O sinal por cabo no Google TV elimina o buffering de handshakes do IPTV.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">DNS Manual no Google TV</h5>
                <p className="text-sm text-muted-foreground">Nas configurações de rede, mude de DHCP para Estático. Defina o DNS para 1.1.1.1. O Google TV por padrão usa o DNS do Google (8.8.8.8), mas o da Cloudflare costuma ter rotas melhores para nossos servidores de sinal.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Info className="w-6 h-6 text-amber-500 mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Remova Dispositivos Bluetooth Ociosos</h5>
                <p className="text-sm text-muted-foreground">Controles de videogame ou fones de ouvido pareados 'acordam' o sistema e causam micro-travas na renderização do vídeo 4K. Desconecte o que não estiver usando.</p>
              </div>
            </li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Central de Ajuda IPTV"
        title="IPTV em Google TV: O Guia de Elite para Performance 2026"
        subtitle="Integração total: aprenda a instalar e tunar o IPTV no sistema Google TV para um streaming 4K inabalável."
        heroImage="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "O Google TV é o que existe de mais moderno em interfaces de Smart TV hoje. Para o usuário da P2 PRO, ele oferece uma experiência de usuário polida e inteligente, mas que exige ajustes finos 'debaixo do capô' para não comprometer a estabilidade do sinal. Se você quer o melhor do mundo Google unido ao melhor servidor de IPTV do Brasil, este é o seu manual definitivo.",
          "Neste guia denso, ensinamos você a dominar o ecossistema Google TV. Exploramos desde a ativação de permissões de segurança até o tuning de memória RAM e a priorização de rede. Aprenda como transformar seu Chromecast ou TV com Google TV em uma verdadeira central de entretenimento profissional.",
          "Prepare-se para navegar pelo catálogo P2 PRO com uma fluidez que você nunca viu antes, aproveitando a inteligência artificial do Google a favor do seu entretenimento."
        ]}
        breadcrumbs={breadcrumbs}
        faq={faq}
        benefits={benefits}
        howItWorks={howItWorks}
        diferenciais={diferenciais}
        sections={sections}
        ctaTitle="🚀 Quer Operar com a Melhor Interface do Mercado?"
        ctaDescription="O Google TV unido aos nossos apps One X e P2 PLAY criam o setup de streaming imbatível. Teste a P2 PRO agora."
        ctaButtonText="Testar P2 PRO no Google TV"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/iptv-google-tv")({
  head: () => ({
    meta: [
      { title: "IPTV em Google TV: Guia Completo e Técnico 2026" },
      { name: "description", content: "Aprenda a instalar e otimizar o IPTV no Google TV e Chromecast 4. Guia técnico sobre permissões, tuning de RAM, limpeza de cache e performance 4K." },
    ],
  }),
  component: ArticlePage,
});
